<template>
  <v-row class="ma-0">
    <span v-if="periodCreated">{{ $t('map.tolldomains.overview.creation_success') }}</span>
    <v-progress-circular
      v-if="loading"
      indeterminate
      color="amber"
    />
    <v-col v-if="!periodCreated">
      <div class="map-container">
        <l-map
          v-if="mapLoaded && index === 4"
          ref="comparison-map"
          :options="{ preferCanvas: true }"
          @ready="onMapReady"
        >
          <l-tile-layer v-bind="tilesOpts" />
        </l-map>
      </div>
    </v-col>
    <v-col v-if="!periodCreated" class="col-5 pa-0 overview-container">
      <span class="confirm-zones">{{ $t('map.tolldomains.overview.confirm_zones') }}</span>
      <v-form v-model="valid">
        <v-row class="overview-card">
          <v-col class="pa-0">
            <v-row class="ma-0">
              <div class="overview-card-title mb-3">{{ $t('common.general') }}</div>
            </v-row>
            <v-row>
              <v-col>
                <div class="mb-1 overview-text-field-label">
                  {{ newVariant ? $t('map.tolldomains.overview.new_variant_name') : $t('map.tolldomains.overview.variant_name') }}
                </div>
                <v-text-field
                  v-model="model.variantName"
                  :disabled="!newVariant"
                  outlined
                  dark
                  dense
                  :rules="nameRules"
                  hide-details="auto"
                />
              </v-col>
              <v-col>
                <div class="mb-1 overview-text-field-label">{{ $t('map.tolldomains.overview.map_name') }}</div>
                <v-text-field
                  v-model="model.geometryName"
                  outlined
                  dark
                  dense
                  :rules="nameRules"
                  hide-details="auto"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <div class="mb-1 overview-text-field-label">{{ $t('map.tolldomains.overview.validity_from') }}</div>
                <DatePicker
                  id="overview-valid-from-datepicker"
                  v-model="model.startTs"
                  class="overview-date-field"
                  :title="$t('map.tolldomains.global_information.validity_must_be_in_future')"
                  :placeholder="format"
                  :disabledDate="disabledDate"
                  :disabledTime="disabledTime"
                  :format="format"
                  :showTime="{ format: 'HH:mm' }"
                  :showToday="false"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-form>
      <v-row class="list-card">
        <v-col class="pa-0">
          <v-row class="ma-0">
            <div class="list-card-title mb-3">{{ $t('map.tolldomains.overview.confirm_zones_list') }}</div>
          </v-row>
          <v-row class="ma-0">
            <div class="list-container">
              <div class="column-header">
                <span>&nbsp;</span>
                <span>{{ $t('map.tolldomains.global_information.tsp_code') }}</span>
                <span>{{ $t('map.tolldomains.global_information.country_code') }}</span>
                <span>{{ $t('map.tolldomains.global_information.provider_identifier') }}</span>
                <span>&nbsp;</span>
              </div>
            </div>
          </v-row>
          <v-row class="ma-0">
            <div class="list-container">
              <div v-for="(zone, index) in zones.zonesChanges.add" :key="zone.code" class="list-item-container">
                <span class="item-header">
                  <v-img
                    contain
                    :src="require('@/assets/zones/star.svg')"
                    :aspect-ratio="4 / 2"
                  /></span>
                <span class="text-capitalize">{{ zone.name }}</span>
                <span class="text-uppercase">{{ zone.code }}</span>
                <span>{{ zone.providerId || 0 }}</span>
                <span class="zoneVisibility" :class="[addZoneVisibility[index] ? 'zoneActive' : 'zoneInactive']" />
              </div>
              <div v-for="(zone, index) in zones.zonesChanges.missing" :key="zone.code" class="list-item-container">
                <span><v-icon color="red">mdi-alert-outline</v-icon></span>
                <span class="text-capitalize">{{ zone.name }}</span>
                <span class="text-uppercase">{{ zone.code }}</span>
                <span>{{ zone.providerId || 0 }}</span>
                <span class="zoneVisibility" :class="[missingZoneVisibility[index] ? 'zoneActive' : 'zoneInactive']" />
              </div>
              <div v-for="(zone, index) in zones.zonesChanges.existing" :key="zone.code" class="list-item-container">
                <span class="item-header">
                  <v-img
                    contain
                    :src="require('@/assets/zones/circle_check.svg')"
                    :aspect-ratio="4 / 2"
                  /></span>
                <span class="text-capitalize">{{ zone.name }}</span>
                <span class="text-uppercase">{{ zone.code }}</span>
                <span>{{ zone.providerId || 0 }}</span>
                <span class="zoneVisibility" :class="[existingZoneVisibility[index] ? 'zoneActive' : 'zoneInactive']" />
              </div>
            </div>
          </v-row>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import { store } from '@/store/modules/tollDomain/types';
import { Variant } from 'ctp-toll-domain';
import L from 'leaflet';
import '@ctp/leaflet-side-by-side';
import { LMap, LTileLayer } from 'vue2-leaflet';
import { MAP_ATTRIBUTION, TILES_URL } from '@/constants/map/mapConstants';
import { GeoJsonObject } from 'geojson';
import { range } from 'lodash';
import moment from 'moment';
import { DatePicker } from 'ant-design-vue';
import Validation from '@/mixins/Validation';
import {
  TOLLDOMAIN_NAME_FORBIDDEN_REGEXP,
  TOLLDOMAIN_NAME_MAX_LENGTH
} from '@/constants/components/tolldomains';

@Component({
  components: {
    DatePicker,
    LMap,
    LTileLayer
  }
})
export default class OverviewConfirmation extends Mixins(Validation) {
  @Prop()
  private index!: number;

  @Prop()
  private zones!: any;

  @Prop()
  private periodCreated!: boolean;

  @Prop()
  private mapLoaded!: boolean;

  @Prop()
  private loading!: boolean;

  @Prop()
  private newVariant!: boolean;

  @store.Getter
  private getVariantById: (id: number) => Variant;

  private geoGroup: L.FeatureGroup;

  private map: L.Map;

  private missingZoneVisibility:boolean[] = [];

  private addZoneVisibility:boolean[] = [];

  private existingZoneVisibility:boolean[] = [];

  private format = 'DD/MM/YYYY HH:mm';

  private model: any = {};

  private valid = false;

  private nameRules = [
    this.requiredRule('common.error.forms.inputs.validator.empty'),
    this.maxLengthRule(TOLLDOMAIN_NAME_MAX_LENGTH, 'common.error.forms.inputs.validator.max_length_field'),
    this.regexpRule(TOLLDOMAIN_NAME_FORBIDDEN_REGEXP, 'common.error.forms.inputs.validator.forbidden_chars')
  ];

  private tilesOpts = {
    url: TILES_URL,
    attribution: MAP_ATTRIBUTION
  };

  private beforeLayers: L.Layer[] = [];

  private afterLayers: L.Layer[] = [];

  private onMapReady() {
    const currentVariant = this.getVariantById(this.zones.variantId);
    this.model.variantName = currentVariant.name;
    if (this.zones.zonesChanges && this.index === 4) {
      const mapComponent = this.$refs['comparison-map'] as LMap;
      mapComponent.mapObject.invalidateSize();
      this.geoGroup = L.featureGroup();
      this.map = mapComponent.mapObject;
      this.map.createPane('left');
      this.map.createPane('right');
      // List of zones to toggle
      this.missingZoneVisibility = Array(this.zones.zonesChanges.missing.length).fill(true);
      this.addZoneVisibility = Array(this.zones.zonesChanges.add.length).fill(true);
      this.existingZoneVisibility = Array(this.zones.zonesChanges.existing.length).fill(true);
      // BEFORE = existing (green)
      this.zones.zonesChanges.existing.forEach((zone: any) => {
        const geo = JSON.parse(zone.geo);
        this.addBeforeLayer(geo, '#94d890');
        // AFTER = existing (green)
        this.addAfterLayer(geo, '#94d890');
      });
      // AFTER = add (orange) + missing (red)
      this.zones.zonesChanges.missing.forEach((zone: any) => {
        const geo = JSON.parse(zone.geo);
        this.addAfterLayer(geo, '#eb4034');
        // BEFORE = existing + missing (green)
        this.addBeforeLayer(geo, '#94d890');
      });
      this.zones.zonesChanges.add.forEach((zone: any) => {
        const geo = JSON.parse(zone.geo);
        this.addAfterLayer(geo, '#ff8c1f');
      });
      const sbs = L.control.sideBySide([], []).addTo(this.map);
      sbs.setLeftLayers(this.beforeLayers);
      sbs.setRightLayers(this.afterLayers);
      this.map.fitBounds(this.geoGroup.getBounds());
    }
  }

  private addBeforeLayer(map: GeoJsonObject, color: string) {
    const layer = L.geoJSON(map, { style: { color, weight: 2, opacity: 1 }, pane: 'left' }).addTo(this.map);
    this.geoGroup.addLayer(layer);
    this.beforeLayers.push(layer);
  }

  private addAfterLayer(map: GeoJsonObject, color: string) {
    const layer = L.geoJSON(map, { style: { color, weight: 2, opacity: 1 }, pane: 'right' }).addTo(this.map);
    this.geoGroup.addLayer(layer);
    this.afterLayers.push(layer);
  }

  private disabledDate(current: moment.Moment) {
    return current.isBefore(moment().startOf('day'));
  }

  private disabledTime(current: moment.Moment) {
    if (current && current.isBefore(moment().startOf('day').add(1, 'day'))) {
      if (current.hours() > moment().hours()) {
        return {
          disabledHours: () => range(0, moment().hours())
        };
      }
      if (current.minutes() === 59) {
        return {
          disabledHours: () => range(0, moment().hours() + 1)
        };
      }
      return {
        disabledHours: () => range(0, moment().hours()),
        disabledMinutes: () => range(0, moment().minutes() + 1)
      };
    }
    return {};
  }

  @Watch('model', { deep: true })
  private updateModel() {
    this.$emit('updateModel', this.index, this.model);
    this.preventConfirm();
  }

  @Watch('valid')
  private preventConfirm() {
    this.$emit('updateError', this.index, !this.valid || !this.model.startTs);
  }
}
</script>
<style lang="scss" scoped>
@import './styles/overview-confirmation.scss';
</style>
